/**
 * StaticPyramidLogo - Non-animated version for emails, PDFs, and static contexts
 * Use this component when animation is not supported (emails, PDFs, print, etc.)
 */

interface StaticPyramidLogoProps {
  width?: number;
  height?: number;
  showText?: boolean;
  showTagline?: boolean;
  variant?: 'default' | 'light';
}

export function StaticPyramidLogo({
  width = 200,
  height = 160,
  showText = true,
  showTagline = true,
  variant = 'default',
}: StaticPyramidLogoProps) {
  const isLight = variant === 'light';
  // 7 flowing bands that sweep upward from left to right
  const bands = [
    { startX: 60, endX: 340, y: 240, thickness: 18 },
    { startX: 80, endX: 320, y: 205, thickness: 16 },
    { startX: 100, endX: 300, y: 172, thickness: 14 },
    { startX: 120, endX: 280, y: 142, thickness: 12 },
    { startX: 140, endX: 260, y: 115, thickness: 10 },
    { startX: 160, endX: 240, y: 92, thickness: 8 },
    { startX: 180, endX: 220, y: 72, thickness: 6 },
  ];

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        userSelect: 'none',
      }}
    >
      {/* Pyramid SVG */}
      <svg
        viewBox="0 0 400 280"
        width={width}
        height={height}
        xmlns="http://www.w3.org/2000/svg"
        style={{ display: 'block' }}
      >
        <defs>
          {/* Gradient - navy or white */}
          <linearGradient id="staticNavyFill" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor={isLight ? '#FFFFFF' : '#1F4B8E'} />
            <stop offset="100%" stopColor={isLight ? '#F0F0F0' : '#2C5BA0'} />
          </linearGradient>
        </defs>

        {/* Flowing ribbon bands - fully rendered */}
        {bands.map((band, i) => {
          const controlY = band.y - 30; // Control point for upward curve

          return (
            <path
              key={i}
              d={`M ${band.startX} ${band.y}
                  Q ${(band.startX + band.endX) / 2} ${controlY}, ${band.endX} ${band.y}
                  Q ${(band.startX + band.endX) / 2} ${controlY + band.thickness}, ${band.startX} ${band.y + band.thickness}
                  Z`}
              fill="url(#staticNavyFill)"
            />
          );
        })}

        {/* Upward arrow at the peak */}
        <path d="M 200 45 L 210 60 L 190 60 Z" fill={isLight ? '#FFFFFF' : '#1F4B8E'} />
      </svg>

      {/* Wordmark + Tagline */}
      {showText && (
        <div style={{ textAlign: 'center', marginTop: '12px' }}>
          <h1
            style={{
              fontFamily: 'serif',
              fontWeight: '600',
              fontSize: '36px',
              color: isLight ? '#FFFFFF' : '#1F4B8E',
              margin: '0 0 4px 0',
            }}
          >
            CapLedger
          </h1>
          {showTagline && (
            <p
              style={{
                marginTop: '4px',
                color: isLight ? '#00BFA5' : '#00BFA5',
                fontWeight: '500',
                fontSize: '14px',
                letterSpacing: '0.05em',
                margin: '4px 0 0 0',
              }}
            >
              Measure Value. Build Trust.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Export as HTML string for email templates
 * Use this function to get the logo as an HTML string for embedding in emails
 */
export function getStaticPyramidLogoHTML(options?: {
  width?: number;
  height?: number;
  showText?: boolean;
  showTagline?: boolean;
}): string {
  const { width = 200, height = 160, showText = true, showTagline = true } = options || {};

  const bands = [
    { startX: 60, endX: 340, y: 240, thickness: 18 },
    { startX: 80, endX: 320, y: 205, thickness: 16 },
    { startX: 100, endX: 300, y: 172, thickness: 14 },
    { startX: 120, endX: 280, y: 142, thickness: 12 },
    { startX: 140, endX: 260, y: 115, thickness: 10 },
    { startX: 160, endX: 240, y: 92, thickness: 8 },
    { startX: 180, endX: 220, y: 72, thickness: 6 },
  ];

  const bandsHTML = bands
    .map((band, i) => {
      const controlY = band.y - 30;
      return `<path d="M ${band.startX} ${band.y} Q ${(band.startX + band.endX) / 2} ${controlY}, ${band.endX} ${band.y} Q ${(band.startX + band.endX) / 2} ${controlY + band.thickness}, ${band.startX} ${band.y + band.thickness} Z" fill="url(#staticNavyFill)" />`;
    })
    .join('');

  return `
    <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; user-select: none;">
      <svg viewBox="0 0 400 280" width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg" style="display: block;">
        <defs>
          <linearGradient id="staticNavyFill" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stop-color="#1F4B8E" />
            <stop offset="100%" stop-color="#2C5BA0" />
          </linearGradient>
        </defs>
        ${bandsHTML}
        <path d="M 200 45 L 210 60 L 190 60 Z" fill="#1F4B8E" />
      </svg>
      ${
        showText
          ? `
        <div style="text-align: center; margin-top: 12px;">
          <h1 style="font-family: serif; font-weight: 600; font-size: 36px; color: #1F4B8E; margin: 0 0 4px 0;">CapLedger</h1>
          ${showTagline ? `<p style="margin-top: 4px; color: #00BFA5; font-weight: 500; font-size: 14px; letter-spacing: 0.05em; margin: 4px 0 0 0;">Measure Value. Build Trust.</p>` : ''}
        </div>
      `
          : ''
      }
    </div>
  `;
}
